import React, { useEffect, useState } from 'react';
import classNames from 'classnames/bind';
import style from '../common/SettingsList.module.scss';
import Layout from '../../common/Layout/Layout';
import Table from '../../common/table/Table';
import Empty from '../../common/empty/Empty';
import Pagination from '../../common/pagination/Pagination';
import dayjs from '../../../util/dayjs';
import ButtonCommon from '../../common/button/ButtonCommon';
import {
  AdministratorAccountLogFilter,
  getAdministratorAccountLogs,
  getAdministratorOptions,
} from '../../../api/function/administrator';
import { AdministratorAccountLogDto, AdministratorOptionDto } from '../../../api/function/administrator/rs';
import useServerPaging from '../common/useServerPaging';

const cx = classNames.bind(style);
const ACTION_TYPES: { value: string; label: string }[] = [
  { value: '', label: '전체' },
  { value: 'CREATE', label: '계정 생성' },
  { value: 'ROLE_CHANGE', label: '권한 변경' },
  { value: 'USE_TYPE_CHANGE', label: '사용 상태 변경' },
  { value: 'LOCK', label: '계정 잠금' },
  { value: 'UNLOCK', label: '잠금 해제' },
  { value: 'PASSWORD_RESET', label: '비밀번호 초기화' },
  { value: 'PASSWORD_CHANGE', label: '비밀번호 변경' },
];

const AccountLogList = () => {
  const [logs, setLogs] = useState<AdministratorAccountLogDto[]>([]);
  const [administrators, setAdministrators] = useState<AdministratorOptionDto[]>([]);
  const [draft, setDraft] = useState<AdministratorAccountLogFilter>({});
  const [filter, setFilter] = useState<AdministratorAccountLogFilter>({});
  const { page, setPage, applyResult, resetToFirst } = useServerPaging();

  useEffect(() => {
    getAdministratorOptions().then((rs) => setAdministrators(rs.data));
  }, []);

  const search = (e: React.FormEvent) => {
    e.preventDefault();
    resetToFirst();
    setFilter({ ...draft });
  };

  useEffect(() => {
    getAdministratorAccountLogs(filter, page.page, page.size)
      .then((rs) => {
        setLogs(rs.data.list);
        applyResult(rs.data.pagination);
      })
      .catch((e) => alert(e?.response?.data?.data?.cause ?? '계정 변경 이력을 불러오지 못했습니다.'));
  }, [filter, page.page, page.size]);

  return (
    <Layout>
      <h2>계정 변경 이력</h2>
      <p className={cx('description')}>
        관리자 계정의 생성, 권한 변경, 사용 중지, 잠금·해제, 비밀번호 초기화·변경 내역입니다. 최근 순으로 보여 줍니다.
      </p>
      <form className={cx('toolbar')} onSubmit={search}>
        <div className={cx('field')}>
          <label htmlFor={'account-log-from'}>기간</label>
          <div className={cx('range')}>
            <input
              id={'account-log-from'}
              type={'date'}
              value={draft.fromDate ?? ''}
              onChange={(e) => setDraft({ ...draft, fromDate: e.target.value })}
            />
            <span>~</span>
            <input
              id={'account-log-to'}
              type={'date'}
              aria-label={'기간 끝'}
              value={draft.toDate ?? ''}
              onChange={(e) => setDraft({ ...draft, toDate: e.target.value })}
            />
          </div>
        </div>
        <div className={cx('field')}>
          <label htmlFor={'account-log-target'}>대상</label>
          <select
            id={'account-log-target'}
            value={draft.administratorId ?? ''}
            onChange={(e) =>
              setDraft({ ...draft, administratorId: e.target.value ? Number(e.target.value) : undefined })
            }
          >
            <option value={''}>전체</option>
            {administrators.map((administrator) => (
              <option key={administrator.id} value={administrator.id}>
                {administrator.name} ({administrator.email})
              </option>
            ))}
          </select>
        </div>
        <div className={cx('field')}>
          <label htmlFor={'account-log-action'}>행위</label>
          <select
            id={'account-log-action'}
            value={draft.actionType ?? ''}
            onChange={(e) => setDraft({ ...draft, actionType: e.target.value })}
          >
            {ACTION_TYPES.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <ButtonCommon size={'small'} submitType>
          조회
        </ButtonCommon>
      </form>
      <Table className={cx('table')}>
        <colgroup>
          <col style={{ width: '16%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '14%' }} />
          <col style={{ width: '42%' }} />
          <col style={{ width: '14%' }} />
        </colgroup>
        <thead>
          <tr>
            <th>일시</th>
            <th>대상</th>
            <th>행위</th>
            <th>내용</th>
            <th>수행자</th>
          </tr>
        </thead>
        <tbody>
          {logs.map((log) => (
            <tr key={log.id}>
              <td>{dayjs(log.createdDateTime).format('YYYY.MM.DD HH:mm')}</td>
              <td>{log.targetAdministratorName}</td>
              <td>{log.actionTypeName}</td>
              <td className={cx('left')}>{log.detail ?? '-'}</td>
              <td>{log.actorAdministratorName ?? '시스템'}</td>
            </tr>
          ))}
          {!logs.length && (
            <tr>
              <td colSpan={5}>
                <Empty />
              </td>
            </tr>
          )}
        </tbody>
      </Table>
      <Pagination page={page} countWord={'건'} pageHandler={setPage} />
    </Layout>
  );
};

export default AccountLogList;
